import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Avatar,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Close } from "@mui/icons-material";
import { tokens } from "../../../../theme";
import apiService from "../../../../service/Api";

interface StreamLeaderboardRow {
  stream_id: number;
  stream_title: string;
  contact_count: number;
}

interface LeaderboardResponse {
  leaderboard: StreamLeaderboardRow[];
  total_participants: number;
}

interface ContactApi {
  id: number;
  contact_type?: string;
  contact_source?: string;
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  suburb?: string | null;
  address_one?: string | null;
  contact_image?: string | null;
}

interface ContactsApiResponse {
  results: ContactApi[];
}

const getContactDisplayName = (c: ContactApi) => {
  if (c.name && c.name.trim().length) return c.name.trim();
  const full = `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim();
  if (full.length) return full;
  return c.email?.trim() || c.phone?.trim() || "Unnamed Contact";
};

const getContactLocation = (c: ContactApi) =>
  [c.address_one, c.suburb, c.city].filter(Boolean).join(", ") || "—";

const StreamsContactsLeaderboard: React.FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [data, setData] = useState<LeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedStream, setSelectedStream] = useState<StreamLeaderboardRow | null>(
    null,
  );
  const [streamContacts, setStreamContacts] = useState<ContactApi[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    let cancelled = false;
    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await apiService.get<LeaderboardResponse>(
          "/api/management/dashboard/streams-contacts-leaderboard/",
        );
        if (!cancelled) setData(res.data);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(
            (err as { message?: string })?.message ||
              "Failed to fetch streams contacts leaderboard.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLeaderboard();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRowClick = async (row: StreamLeaderboardRow) => {
    setSelectedStream(row);
    setPage(0);
    setContactsLoading(true);

    try {
      const res = await apiService.get<ContactsApiResponse>(
        `/api/contacts/contact/?from_stream=${row.stream_id}&page_size=1000`,
      );
      setStreamContacts(res.data.results || []);
    } catch (err) {
      console.error(err);
      setStreamContacts([]);
    } finally {
      setContactsLoading(false);
    }
  };

  const paginatedContacts = useMemo(
    () =>
      streamContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [streamContacts, page, rowsPerPage],
  );

  return (
    <>
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: 0 }}>
        <Typography variant="h5" fontWeight={700}>
          Top Streams by Converted Contacts
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Converted from comments &amp; guestbook visits
        </Typography>

        {loading && <Typography>Loading...</Typography>}
        {error && <Typography color="error">{error}</Typography>}
        {!loading && !error && data?.leaderboard.length === 0 && (
          <Typography color="text.secondary">
            No streams have generated contacts yet.
          </Typography>
        )}

        <Box sx={{ display: "flex", justifyContent: "space-between", px: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            STREAM
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            CONTACTS
          </Typography>
        </Box>

        <List>
          {data?.leaderboard?.map((row, index) => (
            <Box
              key={row.stream_id}
              onClick={() => handleRowClick(row)}
              sx={{ cursor: "pointer" }}
            >
              <ListItem
                sx={{
                  bgcolor: colors.primary[800],
                  borderRadius: 2,
                  my: 1,
                  px: 2,
                  py: 1.5,
                }}
                secondaryAction={
                  <Typography fontWeight={600}>{row.contact_count}</Typography>
                }
              >
                <Typography variant="body1" fontWeight={700} sx={{ width: 30 }}>
                  {index + 1}.
                </Typography>

                <ListItemText
                  primary={row.stream_title}
                  primaryTypographyProps={{ fontWeight: 600 }}
                />
              </ListItem>
            </Box>
          ))}
        </List>
      </Card>

      {/* MODAL */}
      <Dialog
        open={!!selectedStream}
        onClose={() => setSelectedStream(null)}
        fullWidth
        maxWidth="md"
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DialogTitle>
            {`${selectedStream?.stream_title ?? ""}'s Contacts`}
          </DialogTitle>
          <IconButton sx={{ mx: 3 }} onClick={() => setSelectedStream(null)}>
            <Close />
          </IconButton>
        </Box>

        <DialogContent>
          {contactsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress />
            </Box>
          ) : streamContacts.length === 0 ? (
            <Typography sx={{ textAlign: "center", py: 3 }}>
              This stream has no converted contacts.
            </Typography>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.primary[800] }}>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Location</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedContacts.map((c) => (
                    <TableRow
                      key={c.id}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: colors.primary[800] },
                        height: 60,
                      }}
                      onClick={() => navigate(`/contacts/view/${c.id}`)}
                    >
                      <TableCell>
                        {c.contact_image ? (
                          <Avatar
                            variant="rounded"
                            src={c.contact_image}
                            sx={{ width: 30, height: 30 }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: 30,
                              height: 30,
                              bgcolor: "grey.300",
                              borderRadius: 1,
                            }}
                          />
                        )}
                      </TableCell>

                      <TableCell>
                        <Typography fontWeight={600}>
                          {getContactDisplayName(c)}
                        </Typography>
                      </TableCell>

                      <TableCell>{c.email || "—"}</TableCell>
                      <TableCell>{c.phone || "—"}</TableCell>
                      <TableCell>{c.contact_type || "—"}</TableCell>
                      <TableCell>{getContactLocation(c)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <TablePagination
            component="div"
            count={streamContacts.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StreamsContactsLeaderboard;
